import { useState, useRef, useEffect } from "react";
import DataHubPage from "../../components/DataHubPage";
import JournalPage from "../../components/JournalPage";
import { supabase } from "../../lib/supabase";
import { mealAccents } from "../../constants/mealAccents";

function SettingsModal({
  showSettingsModal,
  closeSettingsModal,
  isClosingSettingsModal,
  handleSettingsSwipeTouchStart,
  handleSettingsSwipeTouchMove,
  handleSettingsSwipeTouchEnd,
  modalOverlayStyle,
  modalContentStyle,
  isMobile,
  appTheme,
  settingsPage,
  settingsPageDirection,
  goBackSettingsPage,
  getSettingsPageTitle,
  renderSettingsProfileCard,
  renderSettingsRow,
  openSettingsPage,
  setActiveTab,
  profileImage,
  profileName,
  profileEmail,
  isEditingAccount,
  editName,
  setEditName,
  editEmail,
  setEditEmail,
  editNameWarning,
  setEditNameWarning,
  setIsEditingAccount,
  setProfileName,
  setProfileEmail,
  renderSettingsInfoRow,
  defaultServings,
  setDefaultServings,
  dietaryPreference,
  setDietaryPreference,
  measurementUnit,
  setMeasurementUnit,
  showDeleteConfirm,
  setShowDeleteConfirm,
  deleteConfirmText,
  setDeleteConfirmText,
  renderSettingsToggleRow,
  mealReminders,
  setMealReminders,
  breakfastTime,
  lunchTime,
  dinnerTime,
  openMealTimePicker,
  planReminders,
  setPlanReminders,
  programmeReminders,
  setProgrammeReminders,
  appUpdates,
  setAppUpdates,
  recipes,
  plan,
  currentStreak,
  supportMessages,
  supportInput,
  setSupportInput,
  supportIsTyping,
  supportError,
  sendSupportMessage,
  expandedFaqs,
  setExpandedFaqs,
  setSettingsPageDirection,
  setSettingsPage,
  renderSettingsIcon,
  FeedbackPage,
  dataHub,
  setDataHub,
  nutritionTargets,
  setNutritionTargets,
  calculateNutritionTargets,
  setPlanToastMessage,
  userId,
  handleProfileImageChange,
  removeProfileImage,
  onSaveProfileImage,
}) {
  const [showSignOutSheet, setShowSignOutSheet] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [signOutError, setSignOutError] = useState("");

  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Edit-name/account sheet
  const [showEditNameSheet, setShowEditNameSheet] = useState(false);
  const [editNameDraft, setEditNameDraft] = useState("");
  const [editNameSaving, setEditNameSaving] = useState(false);
  const [editNameError, setEditNameError] = useState("");
  const [editEmailDraft, setEditEmailDraft] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");
  // Keyboard avoidance: tracks how far up to translate the sheet
  const [editSheetOffset, setEditSheetOffset] = useState(0);

  // Photo picker / crop flow
  const [showPhotoSheet,    setShowPhotoSheet]    = useState(false);
  const [cropSource,        setCropSource]        = useState(null);
  const [showCropView,      setShowCropView]      = useState(false);
  const [cropScale,         setCropScale]         = useState(1);
  const [cropOffset,        setCropOffset]        = useState({ x: 0, y: 0 });
  const photoInputRef   = useRef(null);
  const cropCanvasRef    = useRef(null);
  const cropImageObjRef  = useRef(null);
  const lastTouchRef     = useRef(null);
  const cropFromSheetRef = useRef(false); // true when opened via "Edit photo"

  // Change-password page
  const [currentPw, setCurrentPw]       = useState("");
  const [newPw, setNewPw]               = useState("");
  const [confirmPw, setConfirmPw]       = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw]         = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwError, setPwError]           = useState("");
  const [pwSuccess, setPwSuccess]       = useState(false);
  const [pwSaving, setPwSaving]         = useState(false);

  // ── Keyboard avoidance for the edit account sheet ─────────────────────────
  useEffect(() => {
    if (!showEditNameSheet || !window.visualViewport) {
      setEditSheetOffset(0);
      return;
    }
    const handleResize = () => {
      const keyboardHeight = Math.max(
        0,
        window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop
      );
      setEditSheetOffset(keyboardHeight);
    };
    window.visualViewport.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.visualViewport.removeEventListener("resize", handleResize);
      setEditSheetOffset(0);
    };
  }, [showEditNameSheet]);

  // ── Crop helpers ───────────────────────────────────────────────────────────

  // Load image object whenever a new crop source is set, then open the crop view.
  useEffect(() => {
    if (!cropSource) return;
    const img = new Image();
    img.onload = () => {
      cropImageObjRef.current = img;
      // Auto-fit so the image fills the crop circle on first open.
      const canvasSize = Math.min(window.innerWidth - 48, 360);
      const diameter   = canvasSize - 8;
      const shorter    = Math.min(img.naturalWidth, img.naturalHeight);
      const initScale  = diameter / shorter;
      setCropScale(initScale);
      setCropOffset({ x: 0, y: 0 });
      setShowCropView(true);
      setShowPhotoSheet(false);
    };
    img.src = cropSource;
  }, [cropSource]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render the canvas whenever the crop view is visible or the image transform changes.
  useEffect(() => {
    if (!showCropView || !cropCanvasRef.current || !cropImageObjRef.current) return;
    const canvas = cropCanvasRef.current;
    const ctx    = canvas.getContext('2d');
    const size   = canvas.width;
    const img    = cropImageObjRef.current;

    ctx.clearRect(0, 0, size, size);

    const imgW  = img.naturalWidth  * cropScale;
    const imgH  = img.naturalHeight * cropScale;
    const drawX = size / 2 - imgW / 2 + cropOffset.x;
    const drawY = size / 2 - imgH / 2 + cropOffset.y;
    ctx.drawImage(img, drawX, drawY, imgW, imgH);

    // Dark overlay outside the circle using evenodd fill.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, size, size);
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill('evenodd');
    ctx.restore();

    // Circle guide border.
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [showCropView, cropScale, cropOffset]);

  function handleCropFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    cropFromSheetRef.current = false;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setCropSource(reader.result);
    };
    reader.readAsDataURL(file);
  }

  function exportCrop() {
    const displayCanvas = cropCanvasRef.current;
    if (!displayCanvas || !cropImageObjRef.current) return null;
    const dSize  = displayCanvas.width;
    const eSize  = 400;
    const ratio  = eSize / dSize;
    const out    = document.createElement('canvas');
    out.width    = eSize;
    out.height   = eSize;
    const ctx    = out.getContext('2d');
    ctx.beginPath();
    ctx.arc(eSize / 2, eSize / 2, eSize / 2, 0, Math.PI * 2);
    ctx.clip();
    const img  = cropImageObjRef.current;
    const imgW = img.naturalWidth  * cropScale * ratio;
    const imgH = img.naturalHeight * cropScale * ratio;
    const dx   = eSize / 2 - imgW / 2 + cropOffset.x * ratio;
    const dy   = eSize / 2 - imgH / 2 + cropOffset.y * ratio;
    ctx.drawImage(img, dx, dy, imgW, imgH);
    return out.toDataURL('image/jpeg', 0.85);
  }

  function handleCropTouchStart(e) {
    e.preventDefault();
    if (e.touches.length === 1) {
      lastTouchRef.current = { type: 'drag', x: e.touches[0].clientX, y: e.touches[0].clientY, startOffset: { ...cropOffset } };
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      lastTouchRef.current = { type: 'pinch', startDist: dist, startScale: cropScale };
    }
  }

  function handleCropTouchMove(e) {
    e.preventDefault();
    if (!lastTouchRef.current) return;
    if (e.touches.length === 1 && lastTouchRef.current.type === 'drag') {
      const dx = e.touches[0].clientX - lastTouchRef.current.x;
      const dy = e.touches[0].clientY - lastTouchRef.current.y;
      setCropOffset({ x: lastTouchRef.current.startOffset.x + dx, y: lastTouchRef.current.startOffset.y + dy });
    } else if (e.touches.length === 2 && lastTouchRef.current.type === 'pinch') {
      const dist  = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      const ratio = dist / lastTouchRef.current.startDist;
      setCropScale(Math.max(0.3, Math.min(5, lastTouchRef.current.startScale * ratio)));
    }
  }

  function handleCropMouseDown(e) {
    lastTouchRef.current = { type: 'drag', x: e.clientX, y: e.clientY, startOffset: { ...cropOffset } };
  }

  function handleCropMouseMove(e) {
    if (!lastTouchRef.current || lastTouchRef.current.type !== 'drag') return;
    setCropOffset({ x: lastTouchRef.current.startOffset.x + e.clientX - lastTouchRef.current.x, y: lastTouchRef.current.startOffset.y + e.clientY - lastTouchRef.current.y });
  }

  if (!showSettingsModal) return null;
  return (
  <div
    style={{
      ...modalOverlayStyle,
      alignItems: "stretch",
      justifyContent: "stretch",
      padding: 0,
      animation: isClosingSettingsModal
        ? "settingsOverlayFadeOut 0.2s ease forwards"
        : "settingsOverlayFade 0.2s ease"
    }}
    onClick={closeSettingsModal}
  >
    <div
  className="modal-scroll-hidden"
  onTouchStart={handleSettingsSwipeTouchStart}
  onTouchMove={handleSettingsSwipeTouchMove}
  onTouchEnd={handleSettingsSwipeTouchEnd}
  style={{
    ...modalContentStyle,
    width: "100%",
    maxWidth: "100vw",
    height: "100vh",
        maxHeight: "100vh",
        padding: "0",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        margin: "0",
        borderRadius: isMobile ? "0" : "28px",
        background: appTheme.pageBackground,
        animation: isClosingSettingsModal
          ? "settingsSheetOut 0.22s ease forwards"
          : "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
        transformOrigin: "center bottom"
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
  style={{
    padding: isMobile ? "18px 14px 20px" : "22px 20px 24px",
    overflowX: "hidden",
    maxWidth: "100%",
    boxSizing: "border-box"
  }}
>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1fr 40px",
            alignItems: "center",
            marginBottom: "18px"
          }}
        >
          {settingsPage === "home" ? (
            <button
              onClick={closeSettingsModal}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "999px",
                border: "none",
                background: "transparent",
                fontSize: "24px",
                color: appTheme.primaryText,
                cursor: "pointer"
              }}
            >
              ×
            </button>
          ) : (
            <button
              onClick={() => {
                if (settingsPage === "change-password") {
                  // Return to edit sheet rather than settings home
                  goBackSettingsPage();
                  setEditNameDraft(profileName || "");
                  setEditEmailDraft(profileEmail || "");
                  setEmailSent(false); setEmailError("");
                  setShowEditNameSheet(true);
                } else {
                  goBackSettingsPage();
                }
              }}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "999px",
                border: "none",
                background: "transparent",
                fontSize: "24px",
                color: appTheme.primaryText,
                cursor: "pointer"
              }}
            >
              ‹
            </button>
          )}

          <div
            style={{
              textAlign: "center",
              fontSize: isMobile ? "20px" : "22px",
              fontWeight: "700",
              color: appTheme.primaryText,
              fontFamily: appTheme.fontHeading 
            }}
          >
            {getSettingsPageTitle()}
          </div>

          <div />
        </div>

        <div
          key={settingsPage}
          style={{
            animation:
              settingsPageDirection === "forward"
                ? "settingsPageForward 0.22s ease"
                : "settingsPageBackward 0.22s ease"
          }}
        >
          {settingsPage === "home" ? (
            <div style={{ display: "grid", gap: "12px" }}>

              {/* ── PROFILE ───────────────────────────────────────── */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0 8px", gap: "8px" }}>
                {/* Avatar + camera overlay */}
                <div style={{ position: "relative", width: "80px", height: "80px" }}>
                  <div
                    onClick={() => profileImage ? setShowPhotoSheet(true) : photoInputRef.current?.click()}
                    style={{
                      width: "80px", height: "80px", borderRadius: "999px",
                      background: profileImage ? appTheme.cardBackground : appTheme.accentLight,
                      border: appTheme.accentBorder,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "28px", fontWeight: "700", color: appTheme.accentColor,
                      overflow: "hidden", cursor: "pointer",
                      boxShadow: "0 4px 16px rgba(15,23,42,0.08)",
                    }}
                  >
                    {profileImage ? (
                      <img src={profileImage} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
                    ) : (
                      (() => {
                        const parts = (profileName || "").trim().split(/\s+/).filter(Boolean);
                        if (parts.length === 0) return (
                          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                          </svg>
                        );
                        const initials = parts.length >= 2
                          ? parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
                          : parts[0][0].toUpperCase();
                        return <span style={{ fontFamily: appTheme.fontBody, lineHeight: 1, fontSize: initials.length > 1 ? "22px" : "28px" }}>{initials}</span>;
                      })()
                    )}
                  </div>
                  {/* Camera overlay — always visible */}
                  <button
                    onClick={(e) => { e.stopPropagation(); profileImage ? setShowPhotoSheet(true) : photoInputRef.current?.click(); }}
                    style={{
                      position: "absolute", right: "0px", top: "0px",
                      width: "26px", height: "26px", borderRadius: "999px",
                      background: appTheme.elevatedBackground, border: appTheme.cardBorder,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", boxShadow: "0 2px 6px rgba(15,23,42,0.12)", padding: 0,
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke={appTheme.primaryText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </button>
                </div>
                {/* File input — now feeds the crop view instead of saving directly */}
                <input ref={photoInputRef} type="file" accept="image/*" onChange={handleCropFileChange} style={{ display: "none" }} />

                {/* Name + pencil */}
                <button
                  onClick={() => { setEditNameDraft(profileName || ""); setEditEmailDraft(profileEmail || ""); setEmailSent(false); setEmailError(""); setShowEditNameSheet(true); }}
                  style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", padding: "2px 0" }}
                >
                  <span style={{ fontFamily: appTheme.fontHeading, fontSize: "20px", fontWeight: "600", color: appTheme.primaryText }}>
                    {profileName || "Your Name"}
                  </span>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={appTheme.mutedText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>

                {/* Email */}
                <div style={{ fontSize: "13px", color: appTheme.mutedText }}>{profileEmail}</div>
              </div>


              {/* Preferences / Notifications — no label, small gap above */}
              {/* DATA HUB DISABLED — re-enable for V2 */}
              <div style={{ marginTop: "4px", background: appTheme.cardBackground, borderRadius: "24px", border: appTheme.cardBorder, overflow: "hidden" }}>
                {/* {renderSettingsRow("Data Hub", "datahub", false, () => openSettingsPage("datahub"))} */}
                {/* <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 18px" }} /> */}
                {renderSettingsRow("Preferences", "preferences", false, () => openSettingsPage("preferences"))}
                <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 18px" }} />
                {renderSettingsRow("Notifications", "notifications", false, () => openSettingsPage("notifications"))}
              </div>

              {/* My Journal */}
              <div style={{ background: appTheme.cardBackground, borderRadius: "24px", border: appTheme.cardBorder, overflow: "hidden" }}>
                <button
                  onClick={() => openSettingsPage("journal")}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "10px", height: "10px", borderRadius: "999px", background: "rgba(180, 160, 210, 0.8)", flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: "15px", fontWeight: "600", color: appTheme.primaryText }}>My Journal</div>
                      <div style={{ fontSize: "12px", color: appTheme.mutedText, marginTop: "1px", fontFamily: appTheme.fontBody }}>Your daily notes and reflections</div>
                    </div>
                  </div>
                  <div style={{ fontSize: "22px", color: appTheme.mutedText }}>›</div>
                </button>
              </div>

              {/* SUPPORT & LEGAL */}
              <div style={{ paddingLeft: "4px", fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: appTheme.secondaryText }}>Support &amp; Legal</div>
              <div style={{ background: appTheme.cardBackground, borderRadius: "24px", border: appTheme.cardBorder, overflow: "hidden" }}>
                {renderSettingsRow("Support & Feedback", "feedback", false, () => openSettingsPage("feedback"))}
                <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 18px" }} />
                {renderSettingsRow("Legal", "privacy", false, () => openSettingsPage("privacy"))}
              </div>

              {/* DANGER ZONE */}
              <div style={{ marginTop: "4px" }}>
                <button onClick={() => setShowSignOutSheet(true)} style={{ width: "100%", border: "none", background: "transparent", padding: "12px 4px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <svg width="16" height="16" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M29.0625 66.8438C29.0625 67.6145 28.7563 68.3538 28.2113 68.8988C27.6663 69.4438 26.927 69.75 26.1562 69.75H2.90625C2.13546 69.75 1.39625 69.4438 0.851221 68.8988C0.306194 68.3538 0 67.6145 0 66.8438V2.90625C0 2.13546 0.306194 1.39625 0.851221 0.851221C1.39625 0.306194 2.13546 0 2.90625 0H26.1562C26.927 0 27.6663 0.306194 28.2113 0.851221C28.7563 1.39625 29.0625 2.13546 29.0625 2.90625C29.0625 3.67704 28.7563 4.41625 28.2113 4.96128C27.6663 5.50631 26.927 5.8125 26.1562 5.8125H5.8125V63.9375H26.1562C26.927 63.9375 27.6663 64.2437 28.2113 64.7887C28.7563 65.3337 29.0625 66.073 29.0625 66.8438ZM68.8999 32.8188L54.3687 18.2876C53.8233 17.7422 53.0837 17.4359 52.3125 17.4359C51.5413 17.4359 50.8017 17.7422 50.2563 18.2876C49.711 18.8329 49.4046 19.5725 49.4046 20.3438C49.4046 21.115 49.711 21.8546 50.2563 22.3999L59.8288 31.9688H26.1562C25.3855 31.9688 24.6462 32.2749 24.1012 32.82C23.5562 33.365 23.25 34.1042 23.25 34.875C23.25 35.6458 23.5562 36.385 24.1012 36.93C24.6462 37.4751 25.3855 37.7812 26.1562 37.7812H59.8288L50.2563 47.3501C49.711 47.8954 49.4046 48.635 49.4046 49.4062C49.4046 50.1775 49.711 50.9171 50.2563 51.4624C50.8017 52.0078 51.5413 52.3141 52.3125 52.3141C53.0837 52.3141 53.8233 52.0078 54.3687 51.4624L68.8999 36.9312C69.1701 36.6613 69.3845 36.3407 69.5308 35.9879C69.677 35.6351 69.7523 35.2569 69.7523 34.875C69.7523 34.4931 69.677 34.1149 69.5308 33.7621C69.3845 33.4093 69.1701 33.0887 68.8999 32.8188Z" fill={appTheme.secondaryText}/>
                  </svg>
                  <span style={{ fontSize: "16px", fontWeight: "600", color: appTheme.secondaryText }}>Sign out</span>
                </button>
                <button onClick={() => setShowDeleteSheet(true)} style={{ width: "100%", border: "none", background: "transparent", padding: "8px 4px 12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#EF4444", opacity: 0.8 }}>Delete account</span>
                </button>
              </div>

              <div style={{ padding: "4px 4px 0", fontSize: "13px", color: appTheme.secondaryText, lineHeight: 1.5 }}>Version 1.0.0</div>
              <div style={{ height: isMobile ? "100px" : "40px" }} />
            </div>
            ) : settingsPage === "journal" ? (
  <JournalPage
    appTheme={appTheme}
    isMobile={isMobile}
    userId={userId}
  />
            ) : settingsPage === "_datahub_disabled_v2" /* DATA HUB DISABLED — re-enable for V2 */ ? (
  <DataHubPage
    appTheme={appTheme}
    isMobile={isMobile}
    dataHub={dataHub}
    setDataHub={setDataHub}
    nutritionTargets={nutritionTargets}
    setNutritionTargets={setNutritionTargets}
    calculateNutritionTargets={calculateNutritionTargets}
  />
          ) : settingsPage === "change-password" ? (
  <div style={{ display: "grid", gap: "16px" }}>
    {/* Subtitle */}
    <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6, paddingTop: "4px" }}>
      Use a minimum of 8 characters.
    </div>

    <div style={{ background: appTheme.cardBackground, borderRadius: "24px", border: appTheme.cardBorder, overflow: "hidden" }}>
      {/* Current password */}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: appTheme.secondaryText, marginBottom: "8px" }}>Current Password</div>
        <div style={{ position: "relative" }}>
          <input
            type={showCurrentPw ? "text" : "password"}
            value={currentPw}
            onChange={(e) => { setCurrentPw(e.target.value); setPwError(""); }}
            placeholder="Current password"
            style={{ width: "100%", boxSizing: "border-box", padding: "14px 46px 14px 16px", borderRadius: "14px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "15px", color: appTheme.primaryText, outline: "none", fontFamily: appTheme.fontBody }}
          />
          <button onClick={() => setShowCurrentPw(p => !p)} type="button" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: appTheme.mutedText }}>
            {showCurrentPw
              ? <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              : <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            }
          </button>
        </div>
      </div>
      <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 16px" }} />
      {/* New password */}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: appTheme.secondaryText, marginBottom: "8px" }}>New Password</div>
        <div style={{ position: "relative" }}>
          <input
            type={showNewPw ? "text" : "password"}
            value={newPw}
            onChange={(e) => { setNewPw(e.target.value); setPwError(""); }}
            placeholder="New password (min. 8 characters)"
            style={{ width: "100%", boxSizing: "border-box", padding: "14px 46px 14px 16px", borderRadius: "14px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "15px", color: appTheme.primaryText, outline: "none", fontFamily: appTheme.fontBody }}
          />
          <button onClick={() => setShowNewPw(p => !p)} type="button" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: appTheme.mutedText }}>
            {showNewPw
              ? <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              : <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            }
          </button>
        </div>
      </div>
      <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 16px" }} />
      {/* Confirm password */}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: appTheme.secondaryText, marginBottom: "8px" }}>Confirm New Password</div>
        <div style={{ position: "relative" }}>
          <input
            type={showConfirmPw ? "text" : "password"}
            value={confirmPw}
            onChange={(e) => { setConfirmPw(e.target.value); setPwError(""); }}
            placeholder="Confirm new password"
            style={{ width: "100%", boxSizing: "border-box", padding: "14px 46px 14px 16px", borderRadius: "14px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "15px", color: appTheme.primaryText, outline: "none", fontFamily: appTheme.fontBody }}
          />
          <button onClick={() => setShowConfirmPw(p => !p)} type="button" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: appTheme.mutedText }}>
            {showConfirmPw
              ? <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              : <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            }
          </button>
        </div>
      </div>
    </div>

    {pwError && (
      <div style={{ fontSize: "13px", color: "#EF4444", padding: "10px 14px", borderRadius: "12px", background: "rgba(239,68,68,0.08)" }}>{pwError}</div>
    )}
    {pwSuccess && (
      <div style={{ fontSize: "13px", color: "#16a34a", padding: "10px 14px", borderRadius: "12px", background: "rgba(22,163,74,0.08)", fontWeight: "600" }}>✓ Password updated successfully.</div>
    )}

    <button
      disabled={pwSaving}
      onClick={() => {
        setPwError(""); setPwSuccess(false);
        if (!newPw || !confirmPw) { setPwError("Please fill in all fields."); return; }
        if (newPw.length < 8) { setPwError("New password must be at least 8 characters."); return; }
        if (newPw !== confirmPw) { setPwError("Passwords do not match."); return; }
        setPwSaving(true);
        supabase.auth.updateUser({ password: newPw })
          .then(({ error }) => {
            setPwSaving(false);
            if (error) {
              setPwError(error.message || "Could not update password. Please try again.");
            } else {
              setPwSuccess(true);
              setCurrentPw(""); setNewPw(""); setConfirmPw("");
              setTimeout(() => { goBackSettingsPage(); }, 1500);
            }
          });
      }}
      style={{ width: "100%", padding: "15px", borderRadius: "14px", border: "none", background: pwSaving ? appTheme.softBackground : appTheme.accentColor, color: pwSaving ? appTheme.mutedText : "#ffffff", fontWeight: "700", fontSize: "16px", cursor: pwSaving ? "not-allowed" : "pointer", transition: "background 0.15s" }}
    >
      {pwSaving ? "Updating…" : "Update Password"}
    </button>

    <div style={{ height: isMobile ? "100px" : "40px" }} />
  </div>

          ) : settingsPage === "preferences" ? (
  <div style={{ display: "grid", gap: "12px" }}>
    <div style={{ background: appTheme.cardBackground, borderRadius: "24px", border: appTheme.cardBorder, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "600", color: appTheme.primaryText, marginBottom: "10px" }}>Default servings</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          {["1", "2"].map((size) => (
            <button key={size} onClick={() => setDefaultServings(size)} style={{ padding: "10px", borderRadius: "14px", border: defaultServings === size ? appTheme.accentBorder : appTheme.pillBorder, background: defaultServings === size ? appTheme.accentColor : appTheme.pillBackground, color: defaultServings === size ? "#ffffff" : appTheme.primaryText, fontWeight: defaultServings === size ? "700" : "500", fontSize: "15px", cursor: "pointer", transition: "all 0.18s ease" }}>{size}</button>
          ))}
        </div>
      </div>
      <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 16px" }} />
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "600", color: appTheme.primaryText, marginBottom: "10px" }}>Dietary preference</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {[{ value: "none", label: "None" }, { value: "high-protein", label: "High Protein" }, { value: "low-calorie", label: "Low Calorie" }, { value: "vegetarian", label: "Vegetarian" }, { value: "vegan", label: "Vegan" }].map(({ value, label }) => (
            <button key={value} onClick={() => setDietaryPreference(value)} style={{ padding: "9px 14px", borderRadius: "999px", border: dietaryPreference === value ? appTheme.accentBorder : appTheme.pillBorder, background: dietaryPreference === value ? appTheme.accentColor : appTheme.pillBackground, color: dietaryPreference === value ? "#ffffff" : appTheme.primaryText, fontWeight: dietaryPreference === value ? "700" : "500", fontSize: "14px", cursor: "pointer", transition: "all 0.18s ease" }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 16px" }} />
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "600", color: appTheme.primaryText, marginBottom: "10px" }}>Measurement units</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          {[{ value: "grams", label: "Grams (g)" }, { value: "ounces", label: "Ounces (oz)" }].map(({ value, label }) => (
            <button key={value} onClick={() => setMeasurementUnit(value)} style={{ padding: "12px 8px", borderRadius: "14px", border: measurementUnit === value ? appTheme.accentBorder : appTheme.pillBorder, background: measurementUnit === value ? appTheme.accentColor : appTheme.pillBackground, color: measurementUnit === value ? "#ffffff" : appTheme.primaryText, fontWeight: measurementUnit === value ? "700" : "500", fontSize: "14px", cursor: "pointer", transition: "all 0.18s ease" }}>{label}</button>
          ))}
        </div>
      </div>
    </div>
    <div style={{ height: isMobile ? "100px" : "40px" }} />
  </div>

          ) : settingsPage === "notifications" ? (
  <div style={{ display: "grid", gap: "12px" }}>

    {/* Meal Reminders */}
    <div style={{
      background: appTheme.cardBackground,
      borderRadius: "24px",
      border: appTheme.cardBorder,
      overflow: "hidden"
    }}>
      <div style={{
        padding: "16px 18px 12px",
        fontSize: "12px",
        fontWeight: "700",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: appTheme.secondaryText
      }}>
        Meal Reminders
      </div>

      {/* Master meal reminders toggle */}
      {renderSettingsToggleRow(
        "Meal reminders",
        mealReminders,
        () => setMealReminders(mealReminders === "On" ? "Off" : "On")
      )}

      {mealReminders === "On" && (
        <div style={{
          padding: "0 16px 6px",
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.6
        }}>
          Set the times you usually eat and we'll remind you when a meal is coming up.
        </div>
      )}

      {mealReminders === "On" && (
        <>
          <div style={{
            height: "1px",
            background: appTheme.cardBorder.replace("1px solid ", ""),
            margin: "8px 16px"
          }} />

          {[
            {
              meal: "breakfast",
              icon: (
                <svg viewBox="0 0 262 262" width="19" height="19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M122.062 34.875V8.71875C122.062 6.40639 122.981 4.18875 124.616 2.55366C126.251 0.91858 128.469 0 130.781 0C133.094 0 135.311 0.91858 136.946 2.55366C138.581 4.18875 139.5 6.40639 139.5 8.71875V34.875C139.5 37.1874 138.581 39.405 136.946 41.0401C135.311 42.6752 133.094 43.5938 130.781 43.5938C128.469 43.5938 126.251 42.6752 124.616 41.0401C122.981 39.405 122.062 37.1874 122.062 34.875ZM200.531 130.781C200.531 144.576 196.44 158.062 188.776 169.532C181.112 181.003 170.219 189.943 157.473 195.222C144.728 200.501 130.704 201.882 117.174 199.191C103.644 196.5 91.2153 189.857 81.4605 180.102C71.7058 170.347 65.0628 157.919 62.3715 144.389C59.6802 130.859 61.0614 116.834 66.3407 104.089C71.6199 91.3439 80.5599 80.4505 92.0302 72.7862C103.501 65.122 116.986 61.0313 130.781 61.0312C149.274 61.0514 167.003 68.4066 180.08 81.4829C193.156 94.5591 200.511 112.289 200.531 130.781ZM183.094 130.781C183.094 120.435 180.026 110.321 174.278 101.718C168.529 93.1152 160.359 86.4102 150.8 82.4508C141.242 78.4914 130.723 77.4554 120.576 79.4739C110.428 81.4924 101.107 86.4747 93.7907 93.7907C86.4747 101.107 81.4924 110.428 79.4739 120.576C77.4554 130.723 78.4914 141.242 82.4508 150.8C86.4102 160.359 93.1152 168.529 101.718 174.278C110.321 180.026 120.435 183.094 130.781 183.094C144.651 183.079 157.948 177.563 167.756 167.756C177.563 157.948 183.079 144.651 183.094 130.781ZM54.8627 67.1998C56.4987 68.8358 58.7176 69.7548 61.0312 69.7548C63.3449 69.7548 65.5638 68.8358 67.1998 67.1998C68.8358 65.5638 69.7548 63.3449 69.7548 61.0312C69.7548 58.7176 68.8358 56.4987 67.1998 54.8627L49.7623 37.4252C48.1263 35.7892 45.9074 34.8702 43.5938 34.8702C41.2801 34.8702 39.0612 35.7892 37.4252 37.4252C35.7892 39.0612 34.8702 41.2801 34.8702 43.5938C34.8702 45.9074 35.7892 48.1263 37.4252 49.7623L54.8627 67.1998ZM54.8627 194.363L37.4252 211.8C35.7892 213.436 34.8702 215.655 34.8702 217.969C34.8702 220.282 35.7892 222.501 37.4252 224.137C39.0612 225.773 41.2801 226.692 43.5938 226.692C45.9074 226.692 48.1263 225.773 49.7623 224.137L67.1998 206.7C68.0098 205.89 68.6524 204.928 69.0908 203.87C69.5292 202.811 69.7548 201.677 69.7548 200.531C69.7548 199.386 69.5292 198.251 69.0908 197.193C68.6524 196.134 68.0098 195.173 67.1998 194.363C66.3897 193.553 65.428 192.91 64.3696 192.472C63.3112 192.033 62.1768 191.808 61.0312 191.808C59.8857 191.808 58.7513 192.033 57.6929 192.472C56.6345 192.91 55.6728 193.553 54.8627 194.363ZM200.531 69.75C201.677 69.7509 202.811 69.5261 203.869 69.0886C204.928 68.651 205.889 68.0092 206.7 67.1998L224.137 49.7623C225.773 48.1263 226.692 45.9074 226.692 43.5938C226.692 41.2801 225.773 39.0612 224.137 37.4252C222.501 35.7892 220.282 34.8702 217.969 34.8702C215.655 34.8702 213.436 35.7892 211.8 37.4252L194.363 54.8627C193.142 56.0821 192.311 57.6362 191.974 59.3284C191.637 61.0206 191.809 62.7747 192.47 64.3687C193.13 65.9627 194.249 67.3249 195.684 68.2828C197.119 69.2407 198.806 69.7514 200.531 69.75ZM206.7 194.363C205.064 192.727 202.845 191.808 200.531 191.808C198.218 191.808 195.999 192.727 194.363 194.363C192.727 195.999 191.808 198.218 191.808 200.531C191.808 202.845 192.727 205.064 194.363 206.7L211.8 224.137C212.61 224.947 213.572 225.59 214.63 226.028C215.689 226.467 216.823 226.692 217.969 226.692C219.114 226.692 220.249 226.467 221.307 226.028C222.366 225.59 223.327 224.947 224.137 224.137C224.947 223.327 225.59 222.366 226.028 221.307C226.467 220.249 226.692 219.114 226.692 217.969C226.692 216.823 226.467 215.689 226.028 214.63C225.59 213.572 224.947 212.61 224.137 211.8L206.7 194.363ZM43.5938 130.781C43.5938 128.469 42.6752 126.251 41.0401 124.616C39.405 122.981 37.1874 122.062 34.875 122.062H8.71875C6.40639 122.062 4.18875 122.981 2.55366 124.616C0.91858 126.251 0 128.469 0 130.781C0 133.094 0.91858 135.311 2.55366 136.946C4.18875 138.581 6.40639 139.5 8.71875 139.5H34.875C37.1874 139.5 39.405 138.581 41.0401 136.946C42.6752 135.311 43.5938 133.094 43.5938 130.781ZM130.781 217.969C128.469 217.969 126.251 218.887 124.616 220.522C122.981 222.157 122.062 224.375 122.062 226.688V252.844C122.062 255.156 122.981 257.374 124.616 259.009C126.251 260.644 128.469 261.562 130.781 261.562C133.094 261.562 135.311 260.644 136.946 259.009C138.581 257.374 139.5 255.156 139.5 252.844V226.688C139.5 224.375 138.581 222.157 136.946 220.522C135.311 218.887 133.094 217.969 130.781 217.969ZM252.844 122.062H226.688C224.375 122.062 222.157 122.981 220.522 124.616C218.887 126.251 217.969 128.469 217.969 130.781C217.969 133.094 218.887 135.311 220.522 136.946C222.157 138.581 224.375 139.5 226.688 139.5H252.844C255.156 139.5 257.374 138.581 259.009 136.946C260.644 135.311 261.562 133.094 261.562 130.781C261.562 128.469 260.644 126.251 259.009 124.616C257.374 122.981 255.156 122.062 252.844 122.062Z"/>
                </svg>
              ),
              label: "Breakfast",
              time: breakfastTime
            },
            {
              meal: "lunch",
              icon: (
                <svg width="17" height="17" viewBox="0 0 136 136" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M130.469 57.4063H5.21875C3.83465 57.4063 2.50724 57.9562 1.52854 58.9349C0.549831 59.9136 0 61.241 0 62.6251C0.0229299 75.036 3.43845 87.2046 9.87721 97.8147C16.316 108.425 25.5329 117.073 36.5312 122.823V125.25C36.5312 128.018 37.6309 130.673 39.5883 132.63C41.5457 134.588 44.2006 135.688 46.9688 135.688H88.7188C91.4869 135.688 94.1418 134.588 96.0992 132.63C98.0566 130.673 99.1562 128.018 99.1562 125.25V122.823C110.155 117.073 119.372 108.425 125.81 97.8147C132.249 87.2046 135.665 75.036 135.688 62.6251C135.688 61.241 135.138 59.9136 134.159 58.9349C133.18 57.9562 131.853 57.4063 130.469 57.4063ZM91.7587 114.813C90.8514 115.229 90.0827 115.898 89.5439 116.738C89.005 117.579 88.7187 118.557 88.7188 119.555V125.25H46.9688V119.568C46.9713 118.567 46.6861 117.587 46.1472 116.744C45.6082 115.901 44.8381 115.23 43.9288 114.813C34.7511 110.589 26.8457 104.026 21.0062 95.7821C15.1666 87.538 11.5982 77.9029 10.6593 67.8438H125.009C124.072 77.9008 120.506 87.5346 114.67 95.7785C108.834 104.022 100.933 110.587 91.7587 114.813ZM37.6859 20.2227C41.1759 15.8781 42.037 12.9426 41.6782 11.6509C41.4043 10.6789 40.1061 10.1766 40.093 10.1766C38.9117 9.78841 37.9079 8.99115 37.2622 7.92843C36.6166 6.86571 36.3716 5.60738 36.5715 4.38007C36.7713 3.15277 37.4028 2.03714 38.3522 1.23413C39.3016 0.431117 40.5065 -0.00650817 41.75 7.31635e-05C42.2763 0.000689372 42.7996 0.0798426 43.3026 0.234917C43.9549 0.437144 49.7673 2.40722 51.6461 8.55882C53.2508 13.8232 51.2873 19.9422 45.8141 26.7527C42.3241 31.0973 41.463 34.0328 41.8218 35.3245C42.0957 36.2965 43.3678 36.7857 43.3809 36.7923C44.56 37.1798 45.5624 37.9749 46.2081 39.0349C46.8539 40.0949 47.1007 41.3503 46.9043 42.5759C46.7079 43.8014 46.0811 44.9168 45.1365 45.722C44.1918 46.5271 42.9912 46.9691 41.75 46.9688C41.2237 46.9682 40.7004 46.8891 40.1974 46.734C39.5451 46.5317 33.7327 44.5617 31.8539 38.4101C30.2492 33.1457 32.2127 27.0267 37.6859 20.2227ZM63.7796 20.2227C67.2697 15.8781 68.1308 12.9426 67.772 11.6509C67.498 10.6789 66.1998 10.1766 66.1868 10.1766C65.0055 9.78841 64.0016 8.99115 63.356 7.92843C62.7103 6.86571 62.4654 5.60738 62.6652 4.38007C62.865 3.15277 63.4965 2.03714 64.4459 1.23413C65.3954 0.431117 66.6003 -0.00650817 67.8438 7.31635e-05C68.3701 0.000689372 68.8934 0.0798426 69.3963 0.234917C70.0487 0.437144 75.8611 2.40722 77.7398 8.55882C79.3446 13.8232 77.381 19.9422 71.9078 26.7527C68.4178 31.0973 67.5567 34.0328 67.9155 35.3245C68.1895 36.2965 69.4616 36.7857 69.4746 36.7923C70.6537 37.1798 71.6561 37.9749 72.3019 39.0349C72.9476 40.0949 73.1945 41.3503 72.9981 42.5759C72.8017 43.8014 72.1749 44.9168 71.2302 45.722C70.2856 46.5271 69.0849 46.9691 67.8438 46.9688C67.3174 46.9682 66.7941 46.8891 66.2912 46.734C65.6388 46.5317 59.8264 44.5617 57.9477 38.4101C56.3429 33.1457 58.3065 27.0267 63.7796 20.2227ZM89.8734 20.2227C93.3634 15.8781 94.2245 12.9426 93.8657 11.6509C93.5918 10.6789 92.2936 10.1766 92.2806 10.1766C91.0992 9.78841 90.0954 8.99115 89.4497 7.92843C88.8041 6.86571 88.5591 5.60738 88.759 4.38007C88.9588 3.15277 89.5903 2.03714 90.5397 1.23413C91.4891 0.431117 92.694 -0.00650817 93.9375 7.31635e-05C94.4638 0.000689372 94.9871 0.0798426 95.4901 0.234917C96.1424 0.437144 101.955 2.40722 103.834 8.55882C105.438 13.8232 103.475 19.9422 98.0016 26.7527C94.5116 31.0973 93.6505 34.0328 94.0093 35.3245C94.2832 36.2965 95.5553 36.7857 95.5684 36.7923C96.7475 37.1798 97.7499 37.9749 98.3956 39.0349C99.0414 40.0949 99.2882 41.3503 99.0918 42.5759C98.8954 43.8014 98.2686 44.9168 97.324 45.722C96.3793 46.5271 95.1787 46.9691 93.9375 46.9688C93.4112 46.9682 92.8879 46.8891 92.3849 46.734C91.7326 46.5317 85.9202 44.5617 84.0414 38.4101C82.4367 33.1457 84.4002 27.0267 89.8734 20.2227Z" fill="#697B6A"/>
                </svg>
              ),
              label: "Lunch",
              time: lunchTime
            },
            {
              meal: "dinner",
              icon: (
                <svg width="17" height="17" viewBox="0 0 121 132" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M26.0938 36.8352V5.33518C26.0938 3.94279 26.6436 2.60744 27.6223 1.62287C28.601 0.638304 29.9285 0.0851795 31.3125 0.0851795C32.6966 0.0851795 34.0241 0.638304 35.0028 1.62287C35.9815 2.60744 36.5313 3.94279 36.5313 5.33518V36.8352C36.5313 38.2276 35.9815 39.5629 35.0028 40.5475C34.0241 41.5321 32.6966 42.0852 31.3125 42.0852C29.9285 42.0852 28.601 41.5321 27.6223 40.5475C26.6436 39.5629 26.0938 38.2276 26.0938 36.8352ZM120.031 5.33518V126.085C120.031 127.478 119.481 128.813 118.503 129.797C117.524 130.782 116.197 131.335 114.813 131.335C113.428 131.335 112.101 130.782 111.122 129.797C110.144 128.813 109.594 127.478 109.594 126.085V94.5852H78.2813C76.8972 94.5852 75.5698 94.0321 74.5911 93.0475C73.6124 92.0629 73.0625 90.7276 73.0625 89.3352C73.3049 76.7593 74.8838 64.2456 77.7725 52.0077C84.1524 25.4361 96.2469 7.62549 112.758 0.511743C113.551 0.169773 114.417 0.0311976 115.277 0.108436C116.137 0.185674 116.964 0.476313 117.685 0.954308C118.406 1.4323 118.997 2.08272 119.407 2.84729C119.816 3.61186 120.031 4.46669 120.031 5.33518ZM109.594 14.4571C88.6079 30.5811 84.4981 69.8577 83.6958 84.0852H109.594V14.4571ZM57.3345 4.47549C57.2323 3.78613 56.9947 3.12414 56.6355 2.52815C56.2763 1.93215 55.8027 1.41406 55.2424 1.00412C54.6821 0.594173 54.0462 0.300572 53.3719 0.140439C52.6976 -0.0196935 51.9983 -0.0431518 51.3149 0.0714321C50.6315 0.186016 49.9776 0.43635 49.3914 0.80783C48.8052 1.17931 48.2983 1.6645 47.9005 2.2351C47.5026 2.8057 47.2216 3.45029 47.074 4.13127C46.9264 4.81225 46.915 5.51598 47.0406 6.20143L52.1875 37.2486C52.1875 42.8182 49.9882 48.1596 46.0734 52.0979C42.1586 56.0361 36.8489 58.2486 31.3125 58.2486C25.7762 58.2486 20.4665 56.0361 16.5517 52.0979C12.6369 48.1596 10.4375 42.8182 10.4375 37.2486L15.578 6.20143C15.7036 5.51598 15.6922 4.81225 15.5446 4.13127C15.3969 3.45029 15.116 2.8057 14.7181 2.2351C14.3202 1.6645 13.8134 1.17931 13.2272 0.80783C12.6409 0.43635 11.9871 0.186016 11.3036 0.0714321C10.6202 -0.0431518 9.92098 -0.0196935 9.24667 0.140439C8.57237 0.300572 7.93651 0.594173 7.37618 1.00412C6.81586 1.41406 6.34228 1.93215 5.98308 2.52815C5.62388 3.12414 5.38624 3.78613 5.28403 4.47549L0.0652827 35.9755C0.0206323 36.2598 -0.00118205 36.5473 4.93521e-05 36.8352C0.0104769 44.2758 2.63498 51.4732 7.40981 57.1557C12.1846 62.8381 18.8024 66.6398 26.0938 67.8889V126.085C26.0938 127.478 26.6436 128.813 27.6223 129.797C28.601 130.782 29.9285 131.335 31.3125 131.335C32.6966 131.335 34.0241 130.782 35.0028 129.797C35.9815 128.813 36.5313 127.478 36.5313 126.085V67.8889C43.8227 66.6398 50.4405 62.8381 55.2153 57.1557C59.9901 51.4732 62.6146 44.2758 62.625 36.8352C62.6241 36.5471 62.6001 36.2597 62.5533 35.9755L57.3345 4.47549Z" fill="#E67260"/>
                </svg>
              ),
              label: "Dinner",
              time: dinnerTime
            }
          ].map(({ meal, icon, label, time }, index, arr) => (
            <div key={meal}>
              <button
                onClick={() => openMealTimePicker(meal)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 16px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  textAlign: "left"
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}>
                  <div style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "999px",
                    background: mealAccents[meal].iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: mealAccents[meal].color,
                    flexShrink: 0
                  }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: appTheme.primaryText
                    }}>
                      {label}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: appTheme.secondaryText,
                      marginTop: "2px"
                    }}>
                      Reminder at {time}
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: "8px 14px",
                  borderRadius: "999px",
                  background: appTheme.accentLight,
                  border: appTheme.accentBorder,
                  fontSize: "14px",
                  fontWeight: "700",
                  color: appTheme.accentColor
                }}>
                  {time}
                </div>
              </button>

              {index < arr.length - 1 && (
                <div style={{
                  height: "1px",
                  background: appTheme.cardBorder.replace("1px solid ", ""),
                  margin: "0 16px"
                }} />
              )}
            </div>
          ))}
        </>
      )}
    </div>

    {/* Other Notifications */}
    <div style={{
      background: appTheme.cardBackground,
      borderRadius: "24px",
      border: appTheme.cardBorder,
      overflow: "hidden"
    }}>
      <div style={{
        padding: "16px 18px 12px",
        fontSize: "12px",
        fontWeight: "700",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: appTheme.secondaryText
      }}>
        Other Notifications
      </div>

      {renderSettingsToggleRow(
        "Plan reminders",
        planReminders,
        () => setPlanReminders(planReminders === "On" ? "Off" : "On")
      )}

      {planReminders === "On" && (
        <div style={{
          padding: "0 16px 10px",
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.6
        }}>
          We'll nudge you if you haven't planned your week yet.
        </div>
      )}

      <div style={{
        height: "1px",
        background: appTheme.cardBorder.replace("1px solid ", ""),
        margin: "0 16px"
      }} />

      {renderSettingsToggleRow(
        "Programme reminders",
        programmeReminders,
        () => setProgrammeReminders(programmeReminders === "On" ? "Off" : "On")
      )}

      {programmeReminders === "On" && (
        <div style={{
          padding: "0 16px 10px",
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.6
        }}>
          Get reminded to check in on your current programme each week.
        </div>
      )}

      <div style={{
        height: "1px",
        background: appTheme.cardBorder.replace("1px solid ", ""),
        margin: "0 16px"
      }} />

      {renderSettingsToggleRow(
        "App updates",
        appUpdates,
        () => setAppUpdates(appUpdates === "On" ? "Off" : "On")
      )}

      {appUpdates === "On" && (
        <div style={{
          padding: "0 16px 10px",
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.6
        }}>
          Find out when new features and improvements are added to Bout Time.
        </div>
      )}
    </div>


    <div style={{ height: isMobile ? "100px" : "40px" }} />
  </div>
          
) : settingsPage === "privacy" ? (
  <div style={{ display: "grid", gap: "12px" }}>

    {/* LEGAL */}
    <div style={{
      background: appTheme.cardBackground,
      borderRadius: "24px",
      border: appTheme.cardBorder,
      overflow: "hidden"
    }}>
      {[
          {
            label: "Privacy Policy",
            description: "How we handle your data",
            onTap: () => openSettingsPage("privacy-policy")
          },
          {
            label: "Terms of Service",
            description: "Rules for using BoutTime",
            onTap: () => openSettingsPage("terms-of-service")
          }
        ].map(({ label, description, onTap }, index) => (
          <div key={label}>
            {index > 0 && (
              <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "0 16px" }} />
            )}
            <button
              onClick={onTap}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                textAlign: "left"
              }}
            >
              <div>
                <div style={{ fontSize: "15px", fontWeight: "600", color: appTheme.primaryText }}>
                  {label}
                </div>
                <div style={{ fontSize: "12px", color: appTheme.secondaryText, marginTop: "2px" }}>
                  {description}
                </div>
              </div>
              <div style={{ fontSize: "22px", color: appTheme.mutedText }}>›</div>
            </button>
          </div>
        ))}
    </div>


    <div style={{ height: isMobile ? "100px" : "40px" }} />

  </div>

          ) : settingsPage === "privacy-policy" ? (
  <div style={{ display: "grid", gap: "12px" }}>
    {[
      {
        title: "1. Who We Are",
        body: `Bout Time is a meal planning application developed by [YOUR NAME / COMPANY NAME] ("we", "us", or "our"), registered in [YOUR COUNTRY]. If you have any questions about this policy, contact us at [YOUR EMAIL ADDRESS].`
      },
      {
        title: "2. What Data We Collect",
        body: `When you use Bout Time, the following information may be collected:\n\n• Account information: your name and email address, stored on our servers when you create an account.\n• App data: your recipes, meal plans, shopping lists, streak history and preferences. This data is stored locally on your device.\n• Feedback and support messages: any messages you send us via the feedback form or support chat are processed via third-party services (EmailJS and Anthropic AI).\n• Usage data: we do not currently collect analytics or usage tracking data.`
      },
      {
        title: "3. How We Use Your Data",
        body: `We use the data we collect to:\n\n• Provide and maintain the Bout Time app\n• Allow you to create and manage your account\n• Respond to support requests and feedback\n• Improve the app over time\n\nWe do not sell, rent or trade your personal data to third parties.`
      },
      {
        title: "4. Third-Party Services",
        body: `Bout Time currently uses the following third-party services:\n\n• EmailJS — used to send feedback and bug reports. Messages you submit via the feedback form are processed by EmailJS. See their privacy policy at emailjs.com.\n• Anthropic AI — used to power the in-app support assistant. Messages you send to the assistant are processed by Anthropic. See their privacy policy at anthropic.com.\n\nThese services may process your data in accordance with their own privacy policies.`
      },
      {
        title: "5. Data Storage and Security",
        body: `Most of your app data (recipes, plans, preferences) is stored locally on your device using browser localStorage and is not transmitted to our servers.\n\nAccount information (name and email) will be stored on secure servers when account features are fully enabled. We take reasonable steps to protect your data, but no method of storage is 100% secure.`
      },
      {
        title: "6. Your Rights",
        body: `Depending on where you are located, you may have the right to:\n\n• Access the personal data we hold about you\n• Request correction of inaccurate data\n• Request deletion of your data\n• Object to or restrict processing of your data\n\nTo exercise any of these rights, contact us at [YOUR EMAIL ADDRESS]. We will respond within 30 days.`
      },
      {
        title: "7. Children's Privacy",
        body: `Bout Time is not intended for children under the age of 13. We do not knowingly collect personal data from children. If you believe a child has provided us with personal information, please contact us at [YOUR EMAIL ADDRESS] and we will delete it.`
      },
      {
        title: "8. Changes to This Policy",
        body: `We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the date at the top of this page. Continued use of the app after changes means you accept the updated policy.`
      },
      {
        title: "9. Contact Us",
        body: `If you have any questions about this Privacy Policy, please contact us at:\n\n[YOUR NAME / COMPANY NAME]\n[YOUR EMAIL ADDRESS]\n[YOUR ADDRESS OR COUNTRY]`
      }
    ].map(({ title, body }) => (
      <div
        key={title}
        style={{
          background: appTheme.cardBackground,
      borderRadius: "24px",
      border: appTheme.cardBorder,
          padding: "16px 18px"
        }}
      >
        <div style={{
          fontSize: "14px",
          fontWeight: "700",
          color: appTheme.primaryText,
          marginBottom: "8px",
          fontFamily: appTheme.fontHeading
        }}>
          {title}
        </div>
        <div style={{
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.7,
          whiteSpace: "pre-line"
        }}>
          {body}
        </div>
      </div>
    ))}

    <div style={{
      padding: "12px 16px",
      borderRadius: "16px",
      background: appTheme.softBackground,
      border: appTheme.pillBorder,
      fontSize: "12px",
      color: appTheme.secondaryText,
      lineHeight: 1.6
    }}>
      Last updated: [DATE]. This policy applies to Bout Time version 1.0 and above.
    </div>

    <div style={{ height: isMobile ? "100px" : "40px" }} />
  </div>

          ) : settingsPage === "terms-of-service" ? (
  <div style={{ display: "grid", gap: "12px" }}>
    {[
      {
        title: "1. Acceptance of Terms",
        body: `By downloading, installing or using Bout Time ("the App"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the App.\n\nThese terms apply to all users of Bout Time. The App is operated by [YOUR NAME / COMPANY NAME] ("we", "us", or "our").`
      },
      {
        title: "2. Use of the App",
        body: `Bout Time is a personal meal planning tool. You may use it for your own personal, non-commercial purposes.\n\nYou must not:\n\n• Use the App for any unlawful purpose\n• Attempt to reverse engineer, copy or redistribute the App\n• Use the App in a way that could harm other users or third parties\n• Submit false, misleading or harmful content via the feedback or support features`
      },
      {
        title: "3. Your Account",
        body: `When account features are enabled, you are responsible for keeping your login credentials secure. You are responsible for all activity that occurs under your account.\n\nIf you believe your account has been compromised, contact us immediately at [YOUR EMAIL ADDRESS].`
      },
      {
        title: "4. Your Content",
        body: `Any recipes, plans or notes you create within Bout Time belong to you. We do not claim ownership of your content.\n\nBy using the support or feedback features, you give us permission to use the content of those messages solely for the purpose of responding to your request and improving the App.`
      },
      {
        title: "5. Third-Party Services",
        body: `Bout Time uses third-party services including EmailJS and Anthropic AI. Your use of these services through the App is subject to their own terms and privacy policies. We are not responsible for the practices of these third parties.`
      },
      {
        title: "6. Nutritional Information",
        body: `Any calorie, macro or nutritional information displayed in Bout Time is provided for general guidance only. It is not medical or dietary advice.\n\nAlways consult a qualified healthcare professional before making significant changes to your diet, especially if you have a medical condition.`
      },
      {
        title: "7. Disclaimer of Warranties",
        body: `Bout Time is provided "as is" without warranties of any kind. We do not guarantee that the App will be error-free, uninterrupted or suitable for your specific needs.\n\nWe are not responsible for any loss of data, missed meals or other issues arising from use of the App.`
      },
      {
        title: "8. Limitation of Liability",
        body: `To the fullest extent permitted by law, [YOUR NAME / COMPANY NAME] shall not be liable for any indirect, incidental or consequential damages arising from your use of Bout Time.\n\nOur total liability to you for any claim shall not exceed the amount you paid (if any) to use the App in the 12 months preceding the claim.`
      },
      {
        title: "9. Changes to These Terms",
        body: `We may update these Terms of Service at any time. We will notify you of significant changes by updating the date at the bottom of this page. Continued use of the App after changes means you accept the updated terms.`
      },
      {
        title: "10. Governing Law",
        body: `These Terms are governed by the laws of [YOUR COUNTRY / JURISDICTION]. Any disputes will be subject to the exclusive jurisdiction of the courts of [YOUR COUNTRY / JURISDICTION].`
      },
      {
        title: "11. Contact Us",
        body: `If you have any questions about these Terms of Service, please contact us at:\n\n[YOUR NAME / COMPANY NAME]\n[YOUR EMAIL ADDRESS]\n[YOUR ADDRESS OR COUNTRY]`
      }
    ].map(({ title, body }) => (
      <div
        key={title}
        style={{
          background: appTheme.cardBackground,
      borderRadius: "24px",
      border: appTheme.cardBorder,
          padding: "16px 18px"
        }}
      >
        <div style={{
          fontSize: "14px",
          fontWeight: "700",
          color: appTheme.primaryText,
          marginBottom: "8px",
          fontFamily: appTheme.fontHeading
        }}>
          {title}
        </div>
        <div style={{
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.7,
          whiteSpace: "pre-line"
        }}>
          {body}
        </div>
      </div>
    ))}

    <div style={{
      padding: "12px 16px",
      borderRadius: "16px",
      background: appTheme.softBackground,
      border: appTheme.pillBorder,
      fontSize: "12px",
      color: appTheme.secondaryText,
      lineHeight: 1.6
    }}>
      Last updated: [DATE]. These terms apply to Bout Time version 1.0 and above.
    </div>

    <div style={{ height: isMobile ? "100px" : "40px" }} />
  </div>

          ) : settingsPage === "feedback" ? (
            <FeedbackPage appTheme={appTheme} isMobile={isMobile} setPlanToastMessage={setPlanToastMessage} />
          ) : (
            <div
              style={{
                display: "grid",
                gap: "10px"
              }}
            >
              <div
                style={{
                  background: appTheme.cardBackground,
      borderRadius: "24px",
      border: appTheme.cardBorder,
                  padding: "18px"
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: appTheme.primaryText,
                    marginBottom: "8px",
                    fontFamily: appTheme.fontHeading
                  }}
                >
                  {getSettingsPageTitle()}
                </div>

                <div
                  style={{
                    fontSize: "14px",
                    color: appTheme.secondaryText,
                    lineHeight: 1.6
                  }}
                >
                  This page is ready for your real settings content. We can now
                  build this section out properly.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* ── Photo action sheet ──────────────────────────────────────────────── */}
    {showPhotoSheet && (
      <>
        <div onClick={(e) => { e.stopPropagation(); setShowPhotoSheet(false); }} style={{ position: "fixed", inset: 0, zIndex: 1700, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }} />
        <div onClick={e => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1701, background: appTheme.pageBackground, borderRadius: "24px 24px 0 0", animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)" }}>
          <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "4px" }}>
            <div style={{ width: "36px", height: "4px", borderRadius: "999px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
          </div>
          <div style={{ padding: "8px 24px 40px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ fontFamily: appTheme.fontHeading, fontSize: "20px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "4px" }}>Profile photo</div>
            <button onClick={() => { photoInputRef.current?.click(); }} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: appTheme.accentBorder, background: appTheme.accentLight, color: appTheme.accentColor, fontWeight: "700", fontSize: "16px", cursor: "pointer" }}>
              Change photo
            </button>
            <button onClick={() => { cropFromSheetRef.current = true; setCropSource(profileImage); }} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.primaryText, fontWeight: "600", fontSize: "16px", cursor: "pointer" }}>
              Edit photo
            </button>
            <button onClick={() => { removeProfileImage(); setShowPhotoSheet(false); }} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: "#EF4444", fontWeight: "600", fontSize: "16px", cursor: "pointer" }}>
              Remove photo
            </button>
          </div>
        </div>
      </>
    )}

    {/* ── Crop view ────────────────────────────────────────────────────────── */}
    {showCropView && (() => {
      const canvasSize = Math.min(window.innerWidth - 48, 360);
      return (
        <div onClick={e => e.stopPropagation()} style={{ position: "fixed", inset: 0, zIndex: 1900, background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
          {/* Top bar */}
          <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "52px 20px 16px", boxSizing: "border-box" }}>
            <button onClick={() => { setShowCropView(false); setCropSource(null); if (cropFromSheetRef.current) { setShowPhotoSheet(true); cropFromSheetRef.current = false; } }} style={{ background: "none", border: "none", color: "#fff", fontSize: "16px", fontWeight: "600", cursor: "pointer", padding: "8px" }}>Cancel</button>
            <span style={{ color: "#fff", fontSize: "17px", fontWeight: "600", fontFamily: appTheme.fontHeading }}>Crop photo</span>
            <button
              onClick={() => {
                const base64 = exportCrop();
                if (base64 && onSaveProfileImage) {
                  onSaveProfileImage(base64);
                }
                setShowCropView(false);
                setCropSource(null);
                setShowPhotoSheet(false);
              }}
              style={{ background: appTheme.accentColor, border: "none", color: "#fff", fontSize: "16px", fontWeight: "700", cursor: "pointer", padding: "8px 16px", borderRadius: "999px" }}
            >Done</button>
          </div>

          {/* Canvas */}
          <canvas
            ref={cropCanvasRef}
            width={canvasSize}
            height={canvasSize}
            onTouchStart={handleCropTouchStart}
            onTouchMove={handleCropTouchMove}
            onTouchEnd={() => { lastTouchRef.current = null; }}
            onMouseDown={handleCropMouseDown}
            onMouseMove={handleCropMouseMove}
            onMouseUp={() => { lastTouchRef.current = null; }}
            onMouseLeave={() => { lastTouchRef.current = null; }}
            style={{ width: canvasSize, height: canvasSize, touchAction: "none", cursor: "grab", borderRadius: "999px", display: "block" }}
          />

          {/* Hint */}
          <div style={{ padding: "20px 20px 48px", color: "rgba(255,255,255,0.55)", fontSize: "13px", textAlign: "center" }}>
            Pinch to zoom · Drag to position
          </div>
        </div>
      );
    })()}

    {/* ── Edit name sheet ─────────────────────────────────────────────────── */}
    {showEditNameSheet && (
      <>
        <div onClick={(e) => { e.stopPropagation(); setShowEditNameSheet(false); setEditNameError(""); setEmailError(""); setEmailSent(false); }} style={{ position: "fixed", inset: 0, zIndex: 1700, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }} />
        <div onClick={e => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1701, background: appTheme.pageBackground, borderRadius: "24px 24px 0 0", animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)", transform: `translateY(-${editSheetOffset}px)`, transition: "transform 0.3s ease" }}>
          <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "4px" }}>
            <div style={{ width: "36px", height: "4px", borderRadius: "999px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
          </div>
          <div style={{ padding: "8px 24px 32px", display: "flex", flexDirection: "column", gap: "14px" }}>
            <div style={{ fontFamily: appTheme.fontHeading, fontSize: "20px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "2px" }}>Edit account</div>
            <div>
              <div style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: appTheme.secondaryText, marginBottom: "8px" }}>Name</div>
              <input type="text" value={editNameDraft} onChange={(e) => { setEditNameDraft(e.target.value); setEditNameError(""); }} placeholder="Your name" autoFocus style={{ width: "100%", boxSizing: "border-box", padding: "14px 16px", borderRadius: "14px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "15px", color: appTheme.primaryText, outline: "none", fontFamily: appTheme.fontBody }} />
            </div>
            <div>
              <div style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: appTheme.secondaryText, marginBottom: "8px" }}>Email</div>
              <input
                type="email"
                value={editEmailDraft}
                onChange={(e) => { setEditEmailDraft(e.target.value); setEmailError(""); setEmailSent(false); }}
                placeholder="Email address"
                disabled={emailSent}
                style={{ width: "100%", boxSizing: "border-box", padding: "14px 16px", borderRadius: "14px", border: appTheme.accentBorder, background: emailSent ? appTheme.softBackground : appTheme.inputBackground, fontSize: "15px", color: emailSent ? appTheme.mutedText : appTheme.primaryText, outline: "none", fontFamily: appTheme.fontBody, cursor: emailSent ? "not-allowed" : "auto" }}
              />
              {emailError && <div style={{ fontSize: "13px", color: "#EF4444", marginTop: "6px" }}>{emailError}</div>}
              {emailSent && (
                <div style={{ fontSize: "13px", color: "#16a34a", marginTop: "8px", lineHeight: 1.5, padding: "10px 14px", borderRadius: "12px", background: "rgba(22,163,74,0.08)" }}>
                  A confirmation link has been sent to <strong>{editEmailDraft.trim()}</strong>. Please check your inbox to confirm the change. Your email will update once confirmed.
                </div>
              )}
            </div>
            <button
              onClick={() => { setShowEditNameSheet(false); setEditNameError(""); setEmailError(""); setEmailSent(false); openSettingsPage("change-password"); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "0", textAlign: "left", fontSize: "14px", fontWeight: "600", color: appTheme.accentColor }}
            >
              Change password →
            </button>
            {editNameError && <div style={{ fontSize: "13px", color: "#EF4444", padding: "10px 14px", borderRadius: "12px", background: "rgba(239,68,68,0.08)" }}>{editNameError}</div>}
            <button
              disabled={editNameSaving}
              onClick={() => {
                const trimmedName  = editNameDraft.trim();
                const trimmedEmail = editEmailDraft.trim();
                const nameChanged  = trimmedName  !== (profileName  || "").trim();
                const emailChanged = trimmedEmail !== (profileEmail || "").trim();

                if (!trimmedName) { setEditNameError("Name can't be empty."); return; }
                if (emailChanged && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
                  setEmailError("Please enter a valid email address."); return;
                }

                setEditNameSaving(true);

                const saveName = () => {
                  if (!nameChanged || !userId) return Promise.resolve();
                  setProfileName(trimmedName);
                  return supabase.from("user_profiles")
                    .upsert({ id: userId, name: trimmedName }, { onConflict: "id" })
                    .then(({ error }) => {
                      if (error) console.error("name save:", error);
                    });
                };

                saveName().then(() => {
                  if (!emailChanged) {
                    // Name only — close normally
                    setShowEditNameSheet(false);
                    setEditNameError("");
                    setEditNameSaving(false);
                    return;
                  }
                  // Trigger email change via Supabase auth
                  supabase.auth.updateUser({ email: trimmedEmail })
                    .then(({ error }) => {
                      setEditNameSaving(false);
                      if (error) {
                        setEmailError(error.message || "Could not send confirmation. Please try again.");
                      } else {
                        setEmailSent(true);
                      }
                    });
                });
              }}
              style={{ width: "100%", padding: "15px", borderRadius: "14px", border: "none", background: editNameSaving ? appTheme.softBackground : appTheme.accentColor, color: editNameSaving ? appTheme.mutedText : "#ffffff", fontWeight: "700", fontSize: "16px", cursor: editNameSaving ? "not-allowed" : "pointer" }}
            >{editNameSaving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </>
    )}

    {/* ── Sign out confirmation sheet ─────────────────────────────────────── */}
    {showSignOutSheet && (
      <>
        <div
          onClick={(e) => { e.stopPropagation(); setShowSignOutSheet(false); setSignOutError(""); }}
          style={{ position: "fixed", inset: 0, zIndex: 1700, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }}
        />
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1701,
            background: appTheme.pageBackground,
            borderRadius: "24px 24px 0 0",
            animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <div style={{ padding: "52px 24px 32px", display: "flex", flexDirection: "column", gap: "0" }}>
            <div style={{ fontFamily: appTheme.fontHeading, fontSize: "22px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "8px" }}>
              Sign out?
            </div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.5, marginBottom: "24px" }}>
              You'll need to sign back in to access your account.
            </div>

            {signOutError && (
              <div style={{ fontSize: "13px", color: "#EF4444", marginBottom: "14px", padding: "10px 14px", borderRadius: "12px", background: "rgba(239,68,68,0.08)" }}>
                {signOutError}
              </div>
            )}

            <button
              disabled={signOutLoading}
              onClick={async () => {
                setSignOutLoading(true);
                setSignOutError("");
                const { error } = await supabase.auth.signOut();
                if (error) {
                  setSignOutError("Something went wrong. Please try again.");
                  setSignOutLoading(false);
                } else {
                  // Preserve device preferences that should survive sign-out.
                  // These are per-device, not per-account — a returning user on the
                  // same device should never see the Plan intro screen again, and
                  // their notification/display preferences should carry over.
                  const DEVICE_PREF_KEYS = [
                    "planningMode",
                    // hasCreatedManualPlan and hasChosenProgramme moved to Supabase user_profiles
                    "defaultServings", "measurementUnit", "servingSizePreference",
                    "breakfastTime", "lunchTime", "dinnerTime",
                    "mealReminders", "planReminders", "programmeReminders", "appUpdates",
                    "checkedItems", "hiddenStaples", "pantryItems", "manualItems",
                    "customStaples", "removedDefaultStaples",
                  ];
                  const preserved = {};
                  DEVICE_PREF_KEYS.forEach(k => {
                    const v = localStorage.getItem(k);
                    if (v !== null) preserved[k] = v;
                  });
                  localStorage.clear(); // removes user data + migration flags
                  Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));
                  sessionStorage.clear();
                  setShowSignOutSheet(false);
                  closeSettingsModal();
                }
              }}
              style={{
                width: "100%", padding: "15px", borderRadius: "14px", border: "none",
                background: signOutLoading ? appTheme.softBackground : appTheme.accentColor,
                color: signOutLoading ? appTheme.mutedText : "#ffffff", fontWeight: "700", fontSize: "16px",
                cursor: signOutLoading ? "not-allowed" : "pointer",
                marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                transition: "background 0.15s",
              }}
            >
              {signOutLoading ? (
                <>
                  <div style={{ width: "16px", height: "16px", borderRadius: "999px", border: "2px solid rgba(0,0,0,0.1)", borderTopColor: appTheme.mutedText, animation: "spin 0.7s linear infinite" }} />
                  Signing out…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M29.0625 66.8438C29.0625 67.6145 28.7563 68.3538 28.2113 68.8988C27.6663 69.4438 26.927 69.75 26.1562 69.75H2.90625C2.13546 69.75 1.39625 69.4438 0.851221 68.8988C0.306194 68.3538 0 67.6145 0 66.8438V2.90625C0 2.13546 0.306194 1.39625 0.851221 0.851221C1.39625 0.306194 2.13546 0 2.90625 0H26.1562C26.927 0 27.6663 0.306194 28.2113 0.851221C28.7563 1.39625 29.0625 2.13546 29.0625 2.90625C29.0625 3.67704 28.7563 4.41625 28.2113 4.96128C27.6663 5.50631 26.927 5.8125 26.1562 5.8125H5.8125V63.9375H26.1562C26.927 63.9375 27.6663 64.2437 28.2113 64.7887C28.7563 65.3337 29.0625 66.073 29.0625 66.8438ZM68.8999 32.8188L54.3687 18.2876C53.8233 17.7422 53.0837 17.4359 52.3125 17.4359C51.5413 17.4359 50.8017 17.7422 50.2563 18.2876C49.711 18.8329 49.4046 19.5725 49.4046 20.3438C49.4046 21.115 49.711 21.8546 50.2563 22.3999L59.8288 31.9688H26.1562C25.3855 31.9688 24.6462 32.2749 24.1012 32.82C23.5562 33.365 23.25 34.1042 23.25 34.875C23.25 35.6458 23.5562 36.385 24.1012 36.93C24.6462 37.4751 25.3855 37.7812 26.1562 37.7812H59.8288L50.2563 47.3501C49.711 47.8954 49.4046 48.635 49.4046 49.4062C49.4046 50.1775 49.711 50.9171 50.2563 51.4624C50.8017 52.0078 51.5413 52.3141 52.3125 52.3141C53.0837 52.3141 53.8233 52.0078 54.3687 51.4624L68.8999 36.9312C69.1701 36.6613 69.3845 36.3407 69.5308 35.9879C69.677 35.6351 69.7523 35.2569 69.7523 34.875C69.7523 34.4931 69.677 34.1149 69.5308 33.7621C69.3845 33.4093 69.1701 33.0887 68.8999 32.8188Z" fill="#ffffff"/>
                  </svg>
                  Sign out
                </>
              )}
            </button>

            <button
              onClick={() => { setShowSignOutSheet(false); setSignOutError(""); }}
              style={{
                width: "100%", padding: "15px", borderRadius: "14px",
                border: appTheme.pillBorder, background: appTheme.pillBackground,
                color: appTheme.primaryText, fontWeight: "600", fontSize: "16px", cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </>
    )}

    {/* ── Delete account confirmation sheet ───────────────────────────────── */}
    {showDeleteSheet && !deleteLoading && (
      <>
        <div
          onClick={(e) => { e.stopPropagation(); setShowDeleteSheet(false); setDeleteError(""); }}
          style={{ position: "fixed", inset: 0, zIndex: 1700, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }}
        />
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1701,
            background: appTheme.pageBackground,
            borderRadius: "24px 24px 0 0",
            animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {/* Drag handle */}
          <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "4px" }}>
            <div style={{ width: "36px", height: "4px", borderRadius: "999px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
          </div>

          <div style={{ padding: "8px 24px 40px", display: "flex", flexDirection: "column" }}>
            <div style={{ fontFamily: appTheme.fontHeading, fontSize: "22px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "8px" }}>
              Delete your account?
            </div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.5, marginBottom: "24px" }}>
              This will permanently delete your account and all your data. This cannot be undone.
            </div>

            {deleteError && (
              <div style={{ fontSize: "13px", color: "#EF4444", marginBottom: "14px", padding: "10px 14px", borderRadius: "12px", background: "rgba(239,68,68,0.08)" }}>
                {deleteError}
              </div>
            )}

            <button
              onClick={async () => {
                setDeleteLoading(true);
                setDeleteError("");
                try {
                  // Deletion is handled server-side by the delete-user Edge Function,
                  // which uses the service_role key to call auth.admin.deleteUser.
                  const { data: fnData, error } = await supabase.functions.invoke("delete-user");
                  if (error) throw error;
                  console.log("delete-user table results:", fnData?.tables);

                  await supabase.auth.signOut();
                  localStorage.clear(); // also removes ls_migrated_v1/v2/v3
                  sessionStorage.clear();
                  closeSettingsModal();
                } catch {
                  setDeleteError("Something went wrong — please try again or contact support.");
                  setDeleteLoading(false);
                }
              }}
              style={{
                width: "100%", padding: "15px", borderRadius: "14px", border: "none",
                background: "#EF4444", color: "#ffffff", fontWeight: "700", fontSize: "16px",
                cursor: "pointer", marginBottom: "10px",
                transition: "background 0.15s",
              }}
            >
              Delete
            </button>

            <button
              onClick={() => { setShowDeleteSheet(false); setDeleteError(""); }}
              style={{
                width: "100%", padding: "15px", borderRadius: "14px",
                border: appTheme.pillBorder, background: appTheme.pillBackground,
                color: appTheme.primaryText, fontWeight: "600", fontSize: "16px", cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </>
    )}

    {/* ── Delete full-screen loading overlay ──────────────────────────────── */}
    {deleteLoading && (
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: "fixed", inset: 0, zIndex: 1800,
          background: "linear-gradient(180deg, #FAF3E8 0%, #F2E8D5 100%)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: "16px", fontFamily: appTheme.fontBody,
        }}
      >
        <div style={{ width: "36px", height: "36px", borderRadius: "999px", border: "3px solid rgba(27,67,50,0.2)", borderTopColor: "#1B4332", animation: "spin 0.8s linear infinite" }} />
        <div style={{ fontSize: "16px", fontWeight: "600", color: appTheme.primaryText }}>Deleting your account…</div>
      </div>
    )}

    {/* Spin keyframe (used by sign-out spinner and delete overlay spinner) */}
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

  </div>
);
}

export default SettingsModal;